import SettingsSaveNav from '@/components/SettingsSaveNav';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useT } from '@/i18n';
import { Block, BlockTitle, Navbar, Page, Preloader, f7 } from 'framework7-react';
import { THEME_IDS, themes, type Theme, type ThemeId } from '@restaurant/types';
import { useEffect, useRef, useState } from 'react';
import { mediaSrc } from '@/lib/media-url';
import { BrandingUploadError, uploadBrandingImage } from '../lib/branding-upload';
import { useRestaurant, useUpdateRestaurant } from '../hooks/useRestaurantSetup';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';

function toast(text: string) {
  f7.toast.show({ text, closeTimeout: 2200, position: 'bottom' });
}

/**
 * A compact theme tile that mimics the actual storefront: a miniature page -
 * top bar, hero card with a dish thumbnail, title/price and an "Order" button -
 * all rendered from the very same theme tokens (`@restaurant/types`) the public
 * site applies, so the operator sees a faithful WYSIWYG. Two per row.
 */
function ThemeTile({
  theme,
  selected,
  onSelect,
}: {
  theme: Theme;
  selected: boolean;
  onSelect: () => void;
}) {
  const t = theme.tokens;
  const radius = t['--radius'];
  const fontLabel = `${theme.fonts.heading.replace('--font-', '')} · ${theme.fonts.body.replace('--font-', '')}`;
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={`theme-tile${selected ? ' theme-tile--selected' : ''}`}
    >
      {selected && (
        <span className="theme-tile-check" aria-hidden="true">
          <i className="f7-icons">checkmark_alt</i>
        </span>
      )}

      {/* Miniature storefront, painted with the theme's real tokens */}
      <div className="theme-mini" style={{ background: t['--background'] }}>
        {/* Top bar: brand mark + nav ticks + a cart pill */}
        <div className="theme-mini-bar">
          <span className="theme-mini-brand" style={{ background: t['--primary'] }} />
          <span className="theme-mini-navline" style={{ background: t['--muted-foreground'] }} />
          <span
            className="theme-mini-navline short"
            style={{ background: t['--muted-foreground'] }}
          />
          <span className="theme-mini-spacer" />
          <span className="theme-mini-cart" style={{ background: t['--primary'] }} />
        </div>

        {/* Hero banner - a soft photo placeholder */}
        <span
          className="theme-mini-hero"
          style={{
            background: t['--muted'],
            borderColor: t['--border'],
            borderRadius: `calc(${radius} * 0.5)`,
          }}
        />

        {/* Title / price row + the Order button */}
        <div className="theme-mini-row">
          <div className="theme-mini-lines">
            <span className="theme-mini-title" style={{ background: t['--foreground'] }} />
            <span className="theme-mini-sub" style={{ background: t['--muted-foreground'] }} />
          </div>
          <span
            className="theme-mini-btn"
            style={{
              background: t['--primary'],
              borderRadius: `calc(${radius} * 0.5)`,
            }}
          />
        </div>
      </div>

      <div className="theme-tile-meta">
        <span className="theme-tile-name">{theme.label}</span>
        <span className="theme-tile-font">{fontLabel}</span>
      </div>
    </button>
  );
}

type ImageKind = 'logo' | 'cover' | 'gallery';

function useObjectUrls() {
  const created = useRef(new Set<string>());
  useEffect(() => {
    const urls = created.current;
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, []);
  return {
    create(file: File) {
      const url = URL.createObjectURL(file);
      created.current.add(url);
      return url;
    },
    revoke(url: string) {
      URL.revokeObjectURL(url);
      created.current.delete(url);
    },
  };
}

function isFileDrag(e: React.DragEvent) {
  return Array.from(e.dataTransfer.types).includes('Files');
}

function pickImageFiles(e: React.DragEvent): File[] {
  return Array.from(e.dataTransfer.files).filter(
    (f) => f.type.startsWith('image/') || /\.(heic|heif)$/i.test(f.name),
  );
}

function useFileDrop(onFiles: (files: File[]) => void, disabled: boolean) {
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  const handlers = {
    onDragEnter: (e: React.DragEvent) => {
      if (disabled || !isFileDrag(e)) return;
      e.preventDefault();
      depth.current += 1;
      setOver(true);
    },
    onDragOver: (e: React.DragEvent) => {
      if (disabled || !isFileDrag(e)) return;
      e.preventDefault();
    },
    onDragLeave: () => {
      if (depth.current > 0) depth.current -= 1;
      if (depth.current === 0) setOver(false);
    },
    onDrop: (e: React.DragEvent) => {
      if (disabled || !isFileDrag(e)) return;
      e.preventDefault();
      depth.current = 0;
      setOver(false);
      const files = pickImageFiles(e);
      if (files.length) onFiles(files);
    },
  };
  return { over, handlers };
}

function BrandingImageSlot({
  kind,
  label,
  hint,
  url,
  busy,
  uploadLabel,
  replaceLabel,
  removeLabel,
  dropHint,
  onPick,
  onRemove,
}: {
  kind: ImageKind;
  label: string;
  hint: string;
  url: string | null;
  busy: boolean;
  uploadLabel: string;
  replaceLabel: string;
  removeLabel: string;
  dropHint: string;
  onPick: (file: File) => void;
  onRemove: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const drop = useFileDrop((files) => onPick(files[0]), busy);
  const roomy = kind !== 'logo';
  return (
    <div className={`branding-image-card${drop.over ? ' is-dragover' : ''}`} {...drop.handlers}>
      <div className="branding-image-head">
        <span className="branding-image-label">{label}</span>
        <span className="branding-image-hint">{hint}</span>
      </div>
      <div className={`branding-image-slot branding-image-slot--${kind}`}>
        <button
          type="button"
          className={`branding-image-frame branding-image-frame--${kind}${url ? ' has-image' : ''}`}
          disabled={busy}
          aria-label={url ? replaceLabel : uploadLabel}
          onClick={() => inputRef.current?.click()}
        >
          {url ? (
            <>
              <img src={url} alt="" />
              {busy ? (
                <span className="branding-image-busy">
                  <Preloader />
                </span>
              ) : (
                <span className="branding-image-replace" aria-hidden="true">
                  {replaceLabel}
                </span>
              )}
            </>
          ) : busy ? (
            <Preloader />
          ) : (
            <>
              <i className="f7-icons">photo_on_rectangle</i>
              <span className="branding-image-frame-label">{uploadLabel}</span>
              {roomy && (
                <>
                  <span className="branding-image-frame-sub">{dropHint}</span>
                  <span className="branding-image-frame-formats">PNG · JPG · WebP</span>
                </>
              )}
            </>
          )}
        </button>
        {url && !busy && (
          <button
            type="button"
            className="branding-image-remove"
            aria-label={removeLabel}
            onClick={onRemove}
          >
            <i className="f7-icons">xmark</i>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) onPick(file);
        }}
      />
    </div>
  );
}

function SortableGalleryTile({
  id,
  url,
  busy,
  onRemove,
}: {
  id: string;
  url: string;
  busy: boolean;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: busy,
  });
  return (
    <div
      ref={setNodeRef}
      className="branding-gallery-tile"
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        touchAction: 'manipulation',
        ...(isDragging ? { zIndex: 10, opacity: 0.85 } : {}),
      }}
      {...attributes}
      {...listeners}
    >
      <img src={url} alt="" draggable={false} />
      <button
        type="button"
        className="branding-gallery-remove"
        disabled={busy}
        onClick={onRemove}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <i className="f7-icons">xmark</i>
      </button>
    </div>
  );
}

function GallerySection({
  label,
  hint,
  urls,
  itemKeys,
  pending,
  busy,
  full,
  addLabel,
  dropHint,
  onAdd,
  onRemove,
  onReorder,
}: {
  label: string;
  hint: string;
  urls: string[];
  itemKeys: string[];
  pending: string[];
  busy: boolean;
  full: boolean;
  addLabel: string;
  dropHint: string;
  onAdd: (files: File[]) => void;
  onRemove: (index: number) => void;
  onReorder: (from: number, to: number) => Promise<void> | void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const drop = useFileDrop(onAdd, busy);
  const [pendingOrder, setPendingOrder] = useState<string[] | null>(null);
  const itemKeysSignature = itemKeys.join('\n');
  useEffect(() => {
    setPendingOrder(null);
  }, [itemKeysSignature]);

  const orderedKeys = pendingOrder ?? itemKeys;
  const urlByKey = new Map(itemKeys.map((k, i) => [k, urls[i]]));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = itemKeys.indexOf(String(active.id));
    const to = itemKeys.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    setPendingOrder(arrayMove(itemKeys, from, to));
    Promise.resolve(onReorder(from, to)).catch(() => setPendingOrder(null));
  }

  return (
    <div className={`branding-image-card${drop.over ? ' is-dragover' : ''}`} {...drop.handlers}>
      <div className="branding-image-head">
        <span className="branding-image-label">{label}</span>
        <span className="branding-image-hint">{hint}</span>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={orderedKeys} strategy={rectSortingStrategy}>
          <div className="branding-gallery-grid">
            {!full && (
              <button
                type="button"
                className="branding-gallery-add"
                disabled={busy}
                onClick={() => inputRef.current?.click()}
              >
                {busy ? <Preloader /> : <i className="f7-icons">photo_on_rectangle</i>}
                <span className="branding-gallery-add-label">{addLabel}</span>
                <span className="branding-gallery-add-sub">{dropHint}</span>
                <span className="branding-gallery-add-formats">PNG · JPG · WebP</span>
              </button>
            )}
            {orderedKeys.map((key) => (
              <SortableGalleryTile
                key={key}
                id={key}
                url={urlByKey.get(key) ?? ''}
                busy={busy}
                onRemove={() => onRemove(itemKeys.indexOf(key))}
              />
            ))}
            {pending.map((src) => (
              <div key={src} className="branding-gallery-tile">
                <img src={src} alt="" />
                <span className="branding-image-busy">
                  <Preloader />
                </span>
              </div>
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
        style={{ display: 'none' }}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length) onAdd(files);
        }}
      />
    </div>
  );
}

export default function BrandingPage() {
  const t = useT();
  const tb = t.restaurantSetupFeature.branding;
  const settingsBackLink = useSettingsDetailBackLink();
  const { data: restaurant, isLoading } = useRestaurant();
  const update = useUpdateRestaurant();

  const [picked, setPicked] = useState<ThemeId | null>(null);
  const [synced, setSynced] = useState(false);
  if (restaurant && !synced) {
    setPicked(THEME_IDS.find((id) => id === restaurant.themeId) ?? null);
    setSynced(true);
  }

  const dirty = picked !== null && picked !== restaurant?.themeId;

  const [busyImage, setBusyImage] = useState<ImageKind | null>(null);
  const objectUrls = useObjectUrls();
  const [previews, setPreviews] = useState<Partial<Record<ImageKind, string>>>({});
  const [galleryPreviews, setGalleryPreviews] = useState<string[]>([]);

  function slotSrc(kind: 'logo' | 'cover'): string | null {
    const key = kind === 'logo' ? restaurant?.logoKey : restaurant?.coverImageKey;
    return previews[kind] ?? (key ? mediaSrc(key, 'small') : null);
  }

  function uploadErrorMessage(err: unknown): string {
    if (err instanceof BrandingUploadError) {
      if (err.code === 'tooLarge') return tb.imageTooLarge;
      if (err.code === 'wrongType') return tb.imageWrongType;
    }
    return tb.imageUploadFailed;
  }

  async function handlePickImage(kind: 'logo' | 'cover', file: File) {
    if (!restaurant) return;
    const preview = objectUrls.create(file);
    setPreviews((p) => ({ ...p, [kind]: preview }));
    setBusyImage(kind);
    try {
      const { key } = await uploadBrandingImage(file, restaurant.id, kind);
      await update.mutateAsync(kind === 'logo' ? { logoKey: key } : { coverImageKey: key });
      toast(tb.imageSavedMessage);
    } catch (err) {
      toast(uploadErrorMessage(err));
    } finally {
      setPreviews((p) => ({ ...p, [kind]: undefined }));
      objectUrls.revoke(preview);
      setBusyImage(null);
    }
  }

  async function handleAddGalleryPhotos(files: File[]) {
    if (!restaurant) return;
    const current = restaurant.galleryKeys ?? [];
    const room = 9 - current.length;
    if (files.length > room) toast(tb.galleryFull);
    const batch = files.slice(0, Math.max(0, room));
    if (batch.length === 0) return;
    const batchPreviews = batch.map((file) => objectUrls.create(file));
    setGalleryPreviews(batchPreviews);
    setBusyImage('gallery');
    try {
      let saved = current;
      for (const [i, file] of batch.entries()) {
        const { key } = await uploadBrandingImage(file, restaurant.id, 'gallery');
        saved = [...saved, key];
        await update.mutateAsync({ galleryKeys: saved });
        setGalleryPreviews(batchPreviews.slice(i + 1));
      }
      toast(tb.imageSavedMessage);
    } catch (err) {
      toast(uploadErrorMessage(err));
    } finally {
      setGalleryPreviews([]);
      batchPreviews.forEach((url) => objectUrls.revoke(url));
      setBusyImage(null);
    }
  }

  async function handleReorderGalleryPhoto(from: number, to: number) {
    if (!restaurant) return;
    setBusyImage('gallery');
    try {
      await update.mutateAsync({ galleryKeys: arrayMove(restaurant.galleryKeys ?? [], from, to) });
    } catch (err) {
      toast(err instanceof Error ? err.message : tb.imageUploadFailed);
      throw err;
    } finally {
      setBusyImage(null);
    }
  }

  async function handleRemoveGalleryPhoto(index: number) {
    if (!restaurant) return;
    setBusyImage('gallery');
    try {
      const next = (restaurant.galleryKeys ?? []).filter((_, i) => i !== index);
      await update.mutateAsync({ galleryKeys: next });
      toast(tb.imageRemovedMessage);
    } catch (err) {
      toast(err instanceof Error ? err.message : tb.imageUploadFailed);
    } finally {
      setBusyImage(null);
    }
  }

  async function handleRemoveImage(kind: ImageKind) {
    setBusyImage(kind);
    try {
      await update.mutateAsync(kind === 'logo' ? { logoKey: null } : { coverImageKey: null });
      toast(tb.imageRemovedMessage);
    } catch (err) {
      toast(err instanceof Error ? err.message : tb.imageUploadFailed);
    } finally {
      setBusyImage(null);
    }
  }

  async function handleSave() {
    if (!picked) return;
    try {
      await update.mutateAsync({ themeId: picked });
      toast(tb.themeSavedMessage);
    } catch (err) {
      toast(err instanceof Error ? err.message : tb.couldNotSaveTheme);
    }
  }

  return (
    <Page name="branding">
      <Navbar title={tb.title} backLink={settingsBackLink}>
        {!isLoading && (
          <SettingsSaveNav isDirty={dirty} saving={update.isPending} onSave={handleSave} />
        )}
      </Navbar>

      {isLoading ? (
        <Block style={{ textAlign: 'center', paddingTop: 40 }}>
          <Preloader />
        </Block>
      ) : (
        <>
          <BlockTitle>{tb.storefrontTheme}</BlockTitle>
          <Block>
            <p
              style={{ margin: 0, color: 'var(--f7-text-color-secondary, #8e8e93)', fontSize: 14 }}
            >
              {tb.storefrontThemeDescription}
            </p>
          </Block>
          <Block>
            <div className="theme-grid">
              {THEME_IDS.map((id) => (
                <ThemeTile
                  key={id}
                  theme={themes[id]}
                  selected={picked === id}
                  onSelect={() => setPicked(id)}
                />
              ))}
            </div>
          </Block>

          <BlockTitle>{tb.brandingImages}</BlockTitle>
          <Block>
            <p
              style={{ margin: 0, color: 'var(--f7-text-color-secondary, #8e8e93)', fontSize: 14 }}
            >
              {tb.brandingImagesDescription}
            </p>
          </Block>
          <Block>
            <BrandingImageSlot
              kind="logo"
              label={tb.logoLabel}
              hint={tb.logoHint}
              url={slotSrc('logo')}
              busy={busyImage === 'logo'}
              uploadLabel={tb.uploadImage}
              replaceLabel={tb.replaceImage}
              removeLabel={tb.removeImage}
              dropHint={tb.dropFilesHint}
              onPick={(file) => handlePickImage('logo', file)}
              onRemove={() => handleRemoveImage('logo')}
            />
            <BrandingImageSlot
              kind="cover"
              label={tb.coverLabel}
              hint={tb.coverHint}
              url={slotSrc('cover')}
              busy={busyImage === 'cover'}
              uploadLabel={tb.uploadImage}
              replaceLabel={tb.replaceImage}
              removeLabel={tb.removeImage}
              dropHint={tb.dropFilesHint}
              onPick={(file) => handlePickImage('cover', file)}
              onRemove={() => handleRemoveImage('cover')}
            />
            <GallerySection
              label={tb.galleryLabel}
              hint={tb.galleryHint}
              urls={(restaurant?.galleryKeys ?? []).map((key) => mediaSrc(key, 'small'))}
              itemKeys={restaurant?.galleryKeys ?? []}
              pending={galleryPreviews}
              busy={busyImage === 'gallery'}
              full={(restaurant?.galleryKeys?.length ?? 0) + galleryPreviews.length >= 9}
              addLabel={tb.addPhotos}
              dropHint={tb.dropFilesHint}
              onAdd={handleAddGalleryPhotos}
              onRemove={handleRemoveGalleryPhoto}
              onReorder={handleReorderGalleryPhoto}
            />
          </Block>
        </>
      )}
    </Page>
  );
}
